import { CommonModule } from "@angular/common";
import { Component, EventEmitter, HostBinding, Input, Output } from "@angular/core";
import { AngularSvgIconModule } from "angular-svg-icon";

/**
 * Default card width: 100% of the viewport minus two grid columns either side (`--col`, the
 * responsive grid column from bc-theme.scss), capped at 400px so wide screens get a phone-sized
 * card. The cap sits here rather than in the host's max-width so an in-flow `width="100%"` card
 * isn't capped.
 */
export const ONBOARDING_CARD_DEFAULT_WIDTH = "min(400px, calc(100vw - 4 * var(--col)))";

/** Which side of an anchored target the pointer triangle faces. Omit entirely for a card with no pointer (Explanation card, Centred modal). */
export type OnboardingModalPointerSide = "top" | "bottom" | "left" | "right";

/**
 * Shared visual shell for every onboarding-specific card in the Figma file (README /
 * Components sheet): Explanation card (node 20:6938), Centred modal (20:7055), and Anchored
 * prompt with/without buttons (20:5582 / 20:6665). All four share one gradient card - eyebrow,
 * close button, heading, one or two body paragraphs - and differ only in whether they carry a
 * footer (none / a primary pill + secondary text link / a primary pill + secondary pill) and
 * whether they need a directional pointer (only when anchored to a target).
 *
 * This is deliberately its own shell rather than a wrapper on the app-wide `BaseModalComponent`:
 * that component is a MatDialog-open()'d dialog by contract (its own doc comment shows the
 * `dialog.open(..., { panelClass: 'base-modal-panel' })` call every consumer makes), whereas two
 * of these four onboarding cards - Explanation card and the Anchored prompt - are explicitly
 * *not* modal (the workbook's Components sheet requires the underlying surface stay usable), so
 * forcing them through a MatDialog-shaped component would be modelling behaviour they must not
 * have. `OnboardingCentredModalComponent` - the one genuine modal of the four - still opens via
 * `MatDialog.open()` exactly as any other dialog does; the backdrop and focus trap that gives it
 * come from CDK's dialog container itself, not from either shell component, so nothing about
 * being a real modal required inheriting BaseModalComponent specifically.
 *
 * Callers: ExplanationCardComponent (no footer, dividerBeforeSecondary), OnboardingCentredModalComponent
 * (secondaryStyle="link", no pointer), CoachMarkPromptComponent (secondaryStyle="pill", pointerSide set),
 * and the H02A Home continuation card in LandingContentComponent (in-flow, cardRole="region").
 */
@Component({
  selector: "app-onboarding-base-modal",
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule],
  templateUrl: "./onboarding-base-modal.component.html",
  styleUrls: ["./onboarding-base-modal.component.scss"],
})
export class OnboardingBaseModalComponent {
  @Input() heading = "";
  @Input() bodyPrimary = "";
  @Input() bodySecondary?: string;
  /** Explanation card only - a divider line between the two body paragraphs. Centred modal's two-paragraph (X-tall) shape has none. */
  @Input() showDividerBeforeSecondary = false;

  /**
   * The card's ARIA role. "dialog" for the overlay cards; an in-flow card that isn't a dialog (the
   * H02A Home continuation card, FLIP-335) passes "region" so it isn't announced as one.
   */
  @Input() cardRole: "dialog" | "region" = "dialog";

  @Input() showEyebrow = true;
  @Input() showClose = true;
  /**
   * The card's CSS width, applied to this component's own host element (the card fills it), so
   * whatever contains the host - a snackbar, overlay pane or dialog - sizes to the card. Defaults to
   * ONBOARDING_CARD_DEFAULT_WIDTH (the viewport minus two grid columns either side, capped at
   * 400px), shared by every overlay card. In-flow cards pass "100%".
   */
  @Input() @HostBinding("style.width") width = ONBOARDING_CARD_DEFAULT_WIDTH;

  /** Set only when this card is CDK-overlay-anchored to a target (CoachMarkPromptComponent). Omit for a free-floating card. */
  @Input() pointerSide?: OnboardingModalPointerSide;
  /**
   * Top/bottom pointers only: px from the card's left edge, so the pointer can face a target that
   * isn't under the card's default pointer spot. Omit for the default position.
   */
  @Input() pointerOffset?: number;

  /** Omit both to render with no footer at all (Explanation card). */
  @Input() primaryLabel?: string;
  @Input() secondaryLabel?: string;
  /** "pill" (Anchored prompt - two solid pills side by side) or "link" (Centred modal - a pill button, then a plain text link below it). */
  @Input() secondaryStyle: "pill" | "link" = "pill";

  /**
   * "default" (12px eyebrow, 12px body - Explanation card, Centred modal) or "compact" (9px
   * eyebrow, 11px body - Anchored prompt). Heading stays 17px in both - the Figma file doesn't
   * vary it. Two separately-named booleans would invite them going out of sync since nothing in
   * the Figma file mixes one card's eyebrow size with another's body size.
   */
  @Input() textDensity: "default" | "compact" = "default";

  /** "center" (Explanation card, Centred modal) or "left" (Anchored prompt) - matches the Figma file's own heading alignment per card type. */
  @Input() headingAlign: "center" | "left" = "center";

  /**
   * In-flow Home cards only (H02A / H02C / H02D). Where the side nav replaces the bottom nav
   * (landscape touch tablets and desktop - the same condition as the nav switch itself) the card
   * becomes a full-width banner: eyebrow, heading and body on the left, the primary pill (with its
   * forward arrow) and any link beneath it on the right. Elsewhere it stays the stacked card. Purely
   * a CSS switch (see `.ob-modal--responsive-wide`), so overlay cards and modals never opt in.
   */
  @Input() responsiveWide = false;

  @Output() dismissed = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();

  get hasFooter(): boolean {
    return !!(this.primaryLabel || this.secondaryLabel);
  }

  /** Top/bottom pointers run along the card's width (28 x 21 box); left/right ones are 21 x 28. */
  get isVerticalPointer(): boolean {
    return this.pointerSide === "top" || this.pointerSide === "bottom";
  }

  /**
   * Rotates the up-pointing pointer path to face `pointerSide`. Done on the path rather than the
   * <svg>, so the <svg> box keeps its unrotated CSS size and position per side.
   */
  get pointerTransform(): string | null {
    switch (this.pointerSide) {
      case "bottom":
        return "rotate(180 14 10.5)";
      case "left":
        return "translate(0 28) rotate(-90)";
      case "right":
        return "translate(21 0) rotate(90)";
      default:
        return null;
    }
  }

  /**
   * Read once at construction (matches the guard in base-modal.component.ts): a static outline
   * replaces the pulse/motion treatment rather than skipping it entirely.
   */
  readonly prefersReducedMotion =
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  onDismiss(): void {
    this.dismissed.emit();
  }

  onPrimary(): void {
    this.primaryAction.emit();
  }

  onSecondary(): void {
    this.secondaryAction.emit();
  }
}
